import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, Chip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Divider, List, ListItem, ListItemText,
} from '@mui/material';
import { Add, Delete, Edit, CheckCircle, Cancel, Payments, Visibility } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { displayName } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = ['meals', 'transport', 'accommodation', 'office_supplies', 'entertainment', 'training', 'communication', 'other'];
const CURRENCIES = ['HKD', 'USD', 'SGD', 'CNY', 'EUR', 'GBP'];

const catLabel = (c: string) => c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtAmt = (amt: number, currency = 'HKD') => `${currency} ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const statusColor: Record<string, any> = {
  draft: 'default', submitted: 'warning', approved: 'success',
  rejected: 'error', paid: 'info',
};

const totalAmount = (claim: any) =>
  (claim.items || []).reduce((sum: number, i: any) => sum + Number(i.amount), 0);

const emptyItem = { category: 'meals', description: '', expenseDate: new Date().toISOString().split('T')[0], amount: '', currency: 'HKD' };

const Expenses: React.FC = () => {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role || '');

  const [tab, setTab] = useState(0);
  const [allClaims, setAllClaims] = useState<any[]>([]);
  const [myClaims, setMyClaims] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Detail view
  const [viewClaim, setViewClaim] = useState<any>(null);

  // Create/edit dialog
  const [dialog, setDialog] = useState(false);
  const [editingClaim, setEditingClaim] = useState<any>(null);
  const [claimTitle, setClaimTitle] = useState('');
  const [claimDesc, setClaimDesc] = useState('');
  const [claimCurrency, setClaimCurrency] = useState('HKD');
  const [claimEmployee, setClaimEmployee] = useState('');
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [saving, setSaving] = useState(false);

  // Reject dialog
  const [rejectDialog, setRejectDialog] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const promises: Promise<any>[] = [apiService.getEmployees().catch(() => [])];
      if (isHR) {
        promises.push(
          apiService.getAllExpenses().catch(() => []),
          apiService.getExpenseStats().catch(() => null),
        );
      }
      if (user?.employee?.id) {
        promises.push(apiService.getEmployeeExpenses(user.employee.id).catch(() => []));
      }
      const results = await Promise.all(promises);
      setEmployees(results[0]);
      if (isHR) {
        setAllClaims(results[1] || []);
        setStats(results[2]);
        setMyClaims(results[3] || []);
      } else {
        setMyClaims(results[1] || []);
      }
    } catch { setError('Failed to load expense data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingClaim(null);
    setClaimTitle(''); setClaimDesc(''); setClaimCurrency('HKD');
    setClaimEmployee(user?.employee?.id || '');
    setItems([{ ...emptyItem }]);
    setDialog(true);
  };

  const openEdit = (claim: any) => {
    setEditingClaim(claim);
    setClaimTitle(claim.title);
    setClaimDesc(claim.description || '');
    setClaimCurrency(claim.currency || 'HKD');
    setClaimEmployee(claim.employeeId);
    setItems(claim.items?.map((i: any) => ({
      category: i.category, description: i.description,
      expenseDate: i.expenseDate, amount: String(i.amount), currency: i.currency,
    })) || [{ ...emptyItem }]);
    setDialog(true);
  };

  const saveClaim = async () => {
    if (!claimTitle.trim() || !claimEmployee) return;
    const validItems = items.filter(i => i.description.trim() && i.amount);
    if (!validItems.length) { setError('Add at least one expense item'); return; }
    setSaving(true);
    try {
      const payload = {
        employeeId: claimEmployee, title: claimTitle,
        description: claimDesc || undefined, currency: claimCurrency,
        items: validItems.map(i => ({ ...i, amount: Number(i.amount) })),
      };
      if (editingClaim) {
        await apiService.updateExpenseClaim(editingClaim.id, payload);
        setSuccess('Claim updated');
      } else {
        await apiService.createExpenseClaim(payload);
        setSuccess('Claim created');
      }
      setDialog(false);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save claim');
    } finally { setSaving(false); }
  };

  const handleSubmit = async (id: string) => {
    try {
      await apiService.submitExpenseClaim(id);
      setSuccess('Claim submitted for approval');
      setViewClaim(null);
      fetchData();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to submit'); }
  };

  const handleApprove = async (id: string) => {
    try {
      await apiService.processExpenseApproval(id, { action: 'approve', approvedById: user?.employee?.id });
      setSuccess('Claim approved');
      setViewClaim(null);
      fetchData();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to approve'); }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    try {
      await apiService.processExpenseApproval(rejectDialog.id, { action: 'reject', rejectionReason: rejectReason });
      setSuccess('Claim rejected');
      setRejectDialog(null); setRejectReason('');
      setViewClaim(null);
      fetchData();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to reject'); }
  };

  const handlePay = async (id: string) => {
    try {
      await apiService.processExpenseApproval(id, { action: 'pay' });
      setSuccess('Claim marked as paid');
      setViewClaim(null);
      fetchData();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to mark paid'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this expense claim?')) return;
    try {
      await apiService.deleteExpenseClaim(id);
      setSuccess('Claim deleted');
      fetchData();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to delete'); }
  };

  const ClaimTable = ({ claims, showEmployee }: { claims: any[]; showEmployee?: boolean }) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {showEmployee && <TableCell>Employee</TableCell>}
            <TableCell>Title</TableCell>
            <TableCell>Items</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {claims.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showEmployee ? 7 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No expense claims found
              </TableCell>
            </TableRow>
          ) : claims.map(c => (
            <TableRow key={c.id} hover>
              {showEmployee && (
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {c.employee ? displayName(c.employee) : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{c.employee?.department?.name}</Typography>
                </TableCell>
              )}
              <TableCell>
                <Typography variant="body2" fontWeight={500}>{c.title}</Typography>
                {c.description && <Typography variant="caption" color="text.secondary">{c.description}</Typography>}
              </TableCell>
              <TableCell>{c.items?.length ?? 0}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {fmtAmt(totalAmount(c), c.currency)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={c.status} color={statusColor[c.status]} size="small" />
              </TableCell>
              <TableCell>{fmtDate(c.createdAt)}</TableCell>
              <TableCell align="right">
                <Tooltip title="View details">
                  <IconButton size="small" onClick={() => setViewClaim(c)}><Visibility fontSize="small" /></IconButton>
                </Tooltip>
                {c.status === 'draft' && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                {c.status === 'draft' && (
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="400px"><CircularProgress /></Box>;

  const pending = allClaims.filter(c => c.status === 'submitted');
  const myDrafts = myClaims.filter(c => c.status === 'draft');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Expense Claims</Typography>
          <Typography variant="body2" color="text.secondary">Submit and manage employee expense reimbursements</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}
          sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
          New Claim
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats (HR only) */}
      {isHR && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Pending Review', value: stats.submitted, color: 'warning.main' },
            { label: 'Approved', value: stats.approved, color: 'success.main' },
            { label: 'Paid', value: stats.paid, color: 'info.main' },
            { label: 'Rejected', value: stats.rejected, color: 'error.main' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card><CardContent>
                <Typography color="text.secondary" variant="body2">{s.label}</Typography>
                <Typography variant="h4" color={s.color}>{s.value}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      )}

      {pending.length > 0 && isHR && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{pending.length} expense claim(s) awaiting your review</strong>
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`My Claims (${myClaims.length})`} />
          {isHR && <Tab label={`All Claims (${allClaims.length})`} />}
          {isHR && <Tab label={`Pending Review (${pending.length})`} />}
        </Tabs>
      </Paper>

      {tab === 0 && <Paper><ClaimTable claims={myClaims} /></Paper>}
      {isHR && tab === 1 && <Paper><ClaimTable claims={allClaims} showEmployee /></Paper>}
      {isHR && tab === 2 && <Paper><ClaimTable claims={pending} showEmployee /></Paper>}

      {/* Detail / Action Dialog */}
      {viewClaim && (
        <Dialog open onClose={() => setViewClaim(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{viewClaim.title}</Typography>
              <Chip label={viewClaim.status} color={statusColor[viewClaim.status]} size="small" />
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Employee</Typography>
                <Typography>{viewClaim.employee ? displayName(viewClaim.employee) : '—'}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                <Typography variant="h6" color="primary">{fmtAmt(totalAmount(viewClaim), viewClaim.currency)}</Typography>
              </Grid>
              {viewClaim.description && (
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{viewClaim.description}</Typography>
                </Grid>
              )}
              {viewClaim.rejectionReason && (
                <Grid size={12}>
                  <Alert severity="error">Rejected: {viewClaim.rejectionReason}</Alert>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Expense Items ({viewClaim.items?.length})</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewClaim.items?.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell><Chip label={catLabel(item.category)} size="small" variant="outlined" /></TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{fmtDate(item.expenseDate)}</TableCell>
                      <TableCell align="right">{fmtAmt(item.amount, item.currency)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fmtAmt(totalAmount(viewClaim), viewClaim.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewClaim(null)}>Close</Button>
            {viewClaim.status === 'draft' && (
              <Button variant="contained" onClick={() => handleSubmit(viewClaim.id)}
                sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
                Submit for Approval
              </Button>
            )}
            {isHR && viewClaim.status === 'submitted' && (
              <>
                <Button variant="outlined" color="error" startIcon={<Cancel />}
                  onClick={() => { setRejectDialog(viewClaim); setRejectReason(''); }}>
                  Reject
                </Button>
                <Button variant="contained" color="success" startIcon={<CheckCircle />}
                  onClick={() => handleApprove(viewClaim.id)}>
                  Approve
                </Button>
              </>
            )}
            {isHR && viewClaim.status === 'approved' && (
              <Button variant="contained" color="info" startIcon={<Payments />}
                onClick={() => handlePay(viewClaim.id)}>
                Mark as Paid
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Expense Claim</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason for rejection" multiline rows={3}
            value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            sx={{ mt: 2 }} required />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}
            disabled={!rejectReason.trim()}>
            Confirm Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingClaim ? 'Edit Expense Claim' : 'New Expense Claim'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isHR && (
              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel>Employee</InputLabel>
                  <Select value={claimEmployee} onChange={e => setClaimEmployee(e.target.value)} label="Employee">
                    {employees.map((emp: any) => <MenuItem key={emp.id} value={emp.id}>{displayName(emp)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={8}>
              <TextField fullWidth label="Claim Title" value={claimTitle}
                onChange={e => setClaimTitle(e.target.value)} required />
            </Grid>
            <Grid size={4}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select value={claimCurrency} onChange={e => setClaimCurrency(e.target.value)} label="Currency">
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Description (optional)" value={claimDesc}
                onChange={e => setClaimDesc(e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Expense Items</Typography>

          {items.map((item, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="flex-start">
              <Grid size={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select value={item.category} label="Category"
                    onChange={e => { const u = [...items]; u[idx] = { ...u[idx], category: e.target.value }; setItems(u); }}>
                    {CATEGORIES.map(c => <MenuItem key={c} value={c}>{catLabel(c)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={4}>
                <TextField fullWidth size="small" label="Description" value={item.description}
                  onChange={e => { const u = [...items]; u[idx] = { ...u[idx], description: e.target.value }; setItems(u); }} />
              </Grid>
              <Grid size={2}>
                <TextField fullWidth size="small" label="Date" type="date" value={item.expenseDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={e => { const u = [...items]; u[idx] = { ...u[idx], expenseDate: e.target.value }; setItems(u); }} />
              </Grid>
              <Grid size={2}>
                <TextField fullWidth size="small" label="Amount" type="number" value={item.amount}
                  onChange={e => { const u = [...items]; u[idx] = { ...u[idx], amount: e.target.value }; setItems(u); }} />
              </Grid>
              <Grid size={1}>
                <IconButton size="small" color="error"
                  onClick={() => setItems(items.filter((_, i) => i !== idx))}
                  disabled={items.length === 1}>
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Button size="small" startIcon={<Add />}
            onClick={() => setItems([...items, { ...emptyItem }])}>
            Add Item
          </Button>

          {items.filter(i => i.amount).length > 0 && (
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              Total: {fmtAmt(items.reduce((s, i) => s + (Number(i.amount) || 0), 0), claimCurrency)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveClaim}
            disabled={saving || !claimTitle.trim() || !claimEmployee}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
            {saving ? <CircularProgress size={20} /> : editingClaim ? 'Save Changes' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
